import {
  FinanceCalcGrid,
  FinanceCalcGridProps,
  OrientationVal,
  TextSize,
} from '@tribal-worldwide-london/ui-component-library-vwg';
import {
  CTA,
  DesignTokenSizeEnum,
  Divider,
  Text,
  TokenTextAppearance,
  TokenTextColor,
  TextTag,
} from '@volkswagen-onehub/components-core';
import { ReactNode } from 'react';
import { useTribalContext } from '@/context/tribal';
import { useConfigurationSpecs } from '@/hooks/useConfigurationSpecs';
import Box from '@/modules/components/Box/Box';
import FullScreenOverlay from '@/modules/components/FullScreenOverlay/FullScreenOverlay';
import OverlayHeader from '@/modules/components/FullScreenOverlay/OverlayHeader';
import SectionHeader from '@/modules/components/SectionHeader/SectionHeader';
import { fuelType, normalizeFuelTypeName } from '@/modules/constants/fuelType';
import useGlobalStore, {
  GlobalStoreStateConfiguration,
} from '@/stores/globalStore';
import { WLTPModal } from '../WLTPModal/WLTPModal';
import { SectionNames } from '@/analytics/sectionNames';
import {
  performVWBasicLayerLoad,
  performVWBasicLayerClose,
} from '@/analytics/events';
import { DisclaimerText } from '@/styles';

interface SpecificationsOverlayProps {
  activeConfiguration: GlobalStoreStateConfiguration;
  handleClose(): void;
}

interface SpecsCategory {
  title: string;
  specs: FinanceCalcGridProps['financeCalc'];
  termsAndConditions?: string | ReactNode;
  moreInfo?: ReactNode;
}

const SpecificationsOverlay = ({
  activeConfiguration,
  handleClose,
}: SpecificationsOverlayProps) => {
  const { contentManager, analyticsManager, analyticsBuilder } =
    useTribalContext();
  const { retailers } = useGlobalStore((state) => state.app);
  const invalidSpecFallback = contentManager.get('spec-missing-feature-label');

  if (!activeConfiguration.pdbData?.technicalSpecifications) return null;


  const fuelTypeName = normalizeFuelTypeName(
    activeConfiguration.pdbData?.technicalSpecifications?.engine?.fuelTypeName
  );

  const {
    getEngineAndTransmissionSpecs,
    getPerformanceSpecs,
    getDimensionsSpecs,
    getLoadingSpecs,
    getConsumptionSpecs,
    getEmissionsSpecs,
    getTaxAndInsuranceSpecs,
    getBatteryRangeSpecs,
  } = useConfigurationSpecs({
    configuration: activeConfiguration,
    invalidSpecFallback,
  });

  const currentDate = new Date().toLocaleDateString('en-GB');

  let emissionsTermsAndConditions =
    fuelTypeName === fuelType.ELECTRIC
      ? 'electric-emissions-terms-and-conditions'
      : fuelTypeName === fuelType.PLUG_IN_HYBRID
      ? 'hybrid-emissions-terms-and-conditions'
      : 'petrol-diesel-emissions-terms-and-conditions';

  emissionsTermsAndConditions = contentManager.getParsedString(
    emissionsTermsAndConditions,
    [
      {
        identifier: 'date',
        value: currentDate,
      },
    ]
  );


  const hasSpecs = activeConfiguration.pdbData?.technicalSpecifications;

  const MoreInfoWLTPModal = () => (
    <WLTPModal
      sectionName={SectionNames.SINGLE_PAGE_SPECIFICATIONS}
      configurations={[activeConfiguration]}
    />
  );

  const engineAndTransmissionSpecs: SpecsCategory = {
    title: contentManager.get('specifications-engine-title'),
    specs: getEngineAndTransmissionSpecs(),
    termsAndConditions:
      fuelTypeName === fuelType.DIESEL ? (
        <>
          {contentManager.get('adblue-terms-and-conditions-1')}{' '}
          <CTA
            tag="a"
            emphasis="tertiary"
            href={contentManager.get('adblue-terms-and-conditions-href')}
          >
            {contentManager.get('adblue-terms-and-conditions-2')}
          </CTA>{' '}
          {contentManager.get('adblue-terms-and-conditions-3')}`
        </>
      ) : null,
  };

  const performanceSpecs: SpecsCategory = {
    title: contentManager.get('specifications-performance-title'),
    specs: getPerformanceSpecs(),
  };

  const dimensionsSpecs: SpecsCategory = {
    title: contentManager.get('specifications-dimensions-title'),
    specs: getDimensionsSpecs(),
  };

  const loadingSpecs: SpecsCategory = {
    title: 'Loading',
    specs: getLoadingSpecs().map(({ label, text }) => ({ label, text })),
  };

  const consumptionSpecs: SpecsCategory = {
    title: contentManager.get('specifications-consumption-title'),
    specs: getConsumptionSpecs(),
    moreInfo: <MoreInfoWLTPModal />,
  };

  const emissionsSpecs: SpecsCategory = {
    title: contentManager.get('specifications-emissions-title'),
    specs: getEmissionsSpecs(),
    termsAndConditions: emissionsTermsAndConditions,
    moreInfo: <MoreInfoWLTPModal />,
  };

  const insuranceSpecs: SpecsCategory = {
    title: contentManager.get('specifications-insurance-title'),
    specs: getTaxAndInsuranceSpecs(),
  };

  const batteryRangeSpecs: SpecsCategory = {
    title: contentManager.get('specifications-electric-battery-range-title'),
    specs: getBatteryRangeSpecs(),
    moreInfo: <MoreInfoWLTPModal />,
  };

  const specs: Array<SpecsCategory> = [
    engineAndTransmissionSpecs,
    performanceSpecs,
    dimensionsSpecs,
    loadingSpecs,
    consumptionSpecs,
    emissionsSpecs,
    batteryRangeSpecs,
    insuranceSpecs,
  ];

  const visibleSpecs = specs.filter((section) => section.specs.length > 0);

  const analyticsCommonFields = {
    sectionName: SectionNames.SINGLE_PAGE_SPECIFICATIONS,
    configuration: activeConfiguration,
    retailers,
    analyticsBuilder,
    analyticsManager,
  };

  return (
    <FullScreenOverlay
      header={<OverlayHeader handleClose={handleClose} headerBorder={false} />}
      openCallback={() => performVWBasicLayerLoad(analyticsCommonFields)}
      closeCallback={() => performVWBasicLayerClose(analyticsCommonFields)}
      gradient
    >
      <Box
        px={DesignTokenSizeEnum.static300}
        b960={{
          paddingLeft: DesignTokenSizeEnum.static750,
          paddingRight: DesignTokenSizeEnum.static750,
        }}
      >
        <Box mb={DesignTokenSizeEnum.dynamic0020}>
          <SectionHeader
            title={contentManager.get('specifications-overlay-title-normal')}
            titleBold={contentManager.get('specifications-overlay-title-bold')}
            titleSize={TokenTextAppearance.headline400}
          />
        </Box>
        <Box
          as={Text}
          appearance={TokenTextAppearance.copy200}
          mb={DesignTokenSizeEnum.dynamic0150}
          mt={DesignTokenSizeEnum.dynamic0020}
        >
          {contentManager.get('specifications-overlay-subtitle')}
        </Box>
        {visibleSpecs.map(({ title, specs, termsAndConditions, moreInfo }) => (
          <Box mb={DesignTokenSizeEnum.static530} key={title}>
            <Box pb={DesignTokenSizeEnum.static530}>
              <Box
                display="flex"
                gap={DesignTokenSizeEnum.static100}
                mb={DesignTokenSizeEnum.static350}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <Box
                  as={Text}
                  appearance={TokenTextAppearance.headline300}
                  bold
                >
                  {title}
                </Box>
                {moreInfo && (
                  <Box style={{ height: 'fit-content' }} position="relative">
                    {moreInfo}
                  </Box>
                )}
              </Box>

              <FinanceCalcGrid
                financeCalc={specs}
                background={false}
                layoutOrientation={OrientationVal.HORIZONTAL}
                itemOrientation={OrientationVal.VERTICAL}
                textSize={TextSize.LG}
              />
              {termsAndConditions && (
                <DisclaimerText>
                  <Text
                    tag={TextTag.p}
                    color={TokenTextColor.inherit}
                    appearance={TokenTextAppearance.copy100}
                  >
                    {termsAndConditions}
                  </Text>
                </DisclaimerText>
              )}
            </Box>
            <Divider />
          </Box>
        ))}
      </Box>
    </FullScreenOverlay>
  );
};

export default SpecificationsOverlay;
